import React, { useCallback, useState } from 'react';
import { UnitFragment } from '../../../generated/graphql';
import { AppBar, Box, Dialog, FormLabel, IconButton, Input, Stack, Toolbar } from '@mui/material';
import { ConfirmationDialog } from '../../../components/confirmation-dialog';
import {
  ArrowBackIcon,
  ArrowForwardIcon,
  CloseIcon,
  DownloadIcon,
  RemoveItemIcon,
  StarEmptyIcon,
  StarFullIcon,
} from '../../../components/icons';
import mime from 'mime-types';

interface UnitPictureViewProps {
  unit: UnitFragment;
  selectedPhoto: number;
  onClose: () => void;
  onDeletePhoto: (photoId: string) => void;
  onSetAsCoverPhoto: (photoId: string) => void;
  onCaptionChange: (photoId: string, caption: string) => void;
}

export function UnitPictureView(props: UnitPictureViewProps) {
  const { unit: unit, selectedPhoto, onClose, onSetAsCoverPhoto, onDeletePhoto, onCaptionChange } = props;
  const [currentIndex, setCurrentIndex] = useState(selectedPhoto);
  const [caption, setCaption] = useState(unit?.photos[currentIndex].desc);
  const [timer, setTimer] = useState<NodeJS.Timeout | null>(null);
  const [deletePhotoId, setDeletePhotoId] = useState<string | null>(null);
  const photos = unit?.photos || [];

  // Delay before cleaning up blob URL to ensure download has started across all browsers
  const BLOB_URL_CLEANUP_DELAY_MS = 1000;

  const handlePrev = () => {
    const idx = currentIndex > 0 ? currentIndex - 1 : photos.length - 1;
    setCurrentIndex(idx);
    setCaption(photos[idx].desc);
  };

  const handleNext = () => {
    const idx = currentIndex < photos.length - 1 ? currentIndex + 1 : 0;
    setCurrentIndex(idx);
    setCaption(photos[idx].desc);
  };

  const handleUpdateCaption = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setCaption(e.target.value);
    if (timer) {
      clearTimeout(timer);
    }
    setTimer(
      setTimeout(() => {
        onCaptionChange(photos[currentIndex].id, e.target.value);
      }, 500),
    );
  };

  const onDownload = useCallback(async (file: UnitFragment['photos'][0]) => {
    try {
      // Fetch the image as a blob
      const response = await fetch(file.url);

      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status} ${response.statusText}`);
      }

      const blob = await response.blob();

      // Create a local object URL from the blob
      const blobUrl = window.URL.createObjectURL(blob);

      // Create a temporary link element to trigger the download
      const link = document.createElement('a');
      link.download = file.name || 'image.' + (mime.extension(file.mimeType) || 'jpg');
      link.href = blobUrl;
      link.click();

      // Clean up the object URL after download has started
      // Using a conservative delay to ensure compatibility across browsers
      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, BLOB_URL_CLEANUP_DELAY_MS);
    } catch (error) {
      console.error('Failed to download image:', error);
    }
  }, []);

  return (
    <Dialog fullScreen open={true} onClose={onClose}>
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
          <Stack direction="row" sx={{ display: 'flex', justifyContent: 'end', width: '100%' }}>
            <IconButton color="inherit" onClick={() => onSetAsCoverPhoto(photos[currentIndex].id)}>
              {unit.coverPhoto && unit.coverPhoto.id !== photos[currentIndex].id ? <StarEmptyIcon /> : <StarFullIcon />}
            </IconButton>
            <IconButton color="inherit" onClick={() => onDownload(photos[currentIndex])}>
              <DownloadIcon />
            </IconButton>
            <IconButton color="inherit" onClick={() => setDeletePhotoId(photos[currentIndex].id)}>
              <RemoveItemIcon />
            </IconButton>
          </Stack>
        </Toolbar>
      </AppBar>

      <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
        <FormLabel>Popis</FormLabel>
        <Input value={caption ?? ''} placeholder="tu môžete zadať popis fotky..." onChange={handleUpdateCaption} />
      </Stack>

      <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: '50%',
            padding: '1rem',
            width: '100%',

            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ backgroundColor: 'white', padding: 1, borderRadius: 1, opacity: 0.5 }}>
            <IconButton color="primary" onClick={handlePrev}>
              <ArrowBackIcon />
            </IconButton>
          </Box>
          <Box sx={{ backgroundColor: 'white', padding: 1, borderRadius: 1, opacity: 0.5 }}>
            <IconButton color="primary" onClick={handleNext}>
              <ArrowForwardIcon />
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ width: '100%', height: '100%', justifyContent: 'center', alignItems: 'start', display: 'flex' }}>
          <img
            src={photos[currentIndex].urlMd ?? photos[currentIndex].url}
            alt={photos[currentIndex].desc ?? undefined}
            style={{
              maxWidth: '100%',
              maxHeight: 600,
              objectFit: 'contain',
            }}
          />
        </Box>
      </Box>
      {deletePhotoId && (
        <ConfirmationDialog
          title="Odstránenie fotky"
          onCancel={() => setDeletePhotoId(null)}
          onConfirm={() => onDeletePhoto(photos[currentIndex].id)}
          message={'Naozaj chcete odstrániť túto fotku ?'}
        />
      )}
    </Dialog>
  );
}
